(function (globalScope) {
  'use strict';

  const ELIGIBLE_SIZES = new Set(['24×36', '36×48']);

  function normalizeSize(value) {
    const match = String(value || '').trim().replace(/\s+/g, '').match(/^(\d+(?:\.\d+)?)[x×](\d+(?:\.\d+)?)$/i);
    if (!match) return '';
    return `${Number(match[1])}×${Number(match[2])}`;
  }

  function isEligibleSize(value) {
    return ELIGIBLE_SIZES.has(normalizeSize(value));
  }

  function initRoot(root) {
    if (!root || root.dataset.roomPreviewInitialized === 'true') return;
    const dialog = root.querySelector('[data-room-preview-dialog]');
    const trigger = root.querySelector('[data-room-preview-trigger]');
    const openButton = root.querySelector('[data-room-preview-open]');
    if (!dialog || !trigger || !openButton) return;

    const variantData = root.querySelector('[data-panel="canvas"] [data-variant-data]');
    const appForm = dialog.querySelector('[data-room-preview-app-form]');
    let variants = [];
    try {
      variants = JSON.parse(variantData ? variantData.textContent : '[]');
    } catch (_) {
      return;
    }

    root.dataset.roomPreviewInitialized = 'true';
    let currentVariant = null;
    let returnFocusTo = null;
    const byId = (id) => variants.find((variant) => String(variant.id) === String(id));
    const currentVariantId = root.querySelector('[data-panel="canvas"] [data-variant-id]')?.value;

    function syncVariant(variant) {
      if (!variant) return;
      currentVariant = byId(variant.id) || variant;
      trigger.hidden = !isEligibleSize(currentVariant.size);
    }

    function firstFormControl() {
      return dialog.querySelector('input:not([type="hidden"]):not([disabled]),select:not([disabled]),textarea:not([disabled]),button:not([disabled])');
    }

    function normalizeText(value) {
      return String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();
    }

    function hideField(control) {
      let field = control.closest('label, [class*="field"], [class*="input"], div');
      while (field && field !== appForm) {
        const text = normalizeText(field.textContent);
        if (text.includes('what did you measure between') && text.length < 220) break;
        field = field.parentElement;
      }
      if (!field || field === appForm) field = control.closest('label') || control.parentElement;
      if (!field) return;
      field.hidden = true;
      field.setAttribute('aria-hidden', 'true');
      field.style.setProperty('display', 'none', 'important');
    }

    function simplifyFormRoot(formRoot) {
      if (!formRoot?.querySelectorAll) return;

      formRoot.querySelectorAll('input, textarea, select').forEach((control) => {
        const id = control.id;
        const label = id ? formRoot.querySelector(`label[for="${globalScope.CSS?.escape ? globalScope.CSS.escape(id) : id}"]`) : null;
        const fieldText = [
          control.getAttribute('aria-label'),
          control.getAttribute('placeholder'),
          control.name,
          label?.textContent,
          control.closest('label')?.textContent,
          control.parentElement?.textContent
        ].map(normalizeText).join(' ');
        if (fieldText.includes('what did you measure between')) hideField(control);
      });

      formRoot.querySelectorAll('h1, h2, h3, [role="heading"]').forEach((heading) => {
        if (normalizeText(heading.textContent) !== 'see the journey home on your wall') return;
        const description = heading.nextElementSibling;
        heading.hidden = true;
        heading.setAttribute('aria-hidden', 'true');
        heading.style.setProperty('display', 'none', 'important');
        if (description && normalizeText(description.textContent).includes('straight-on photo')) {
          description.hidden = true;
          description.setAttribute('aria-hidden', 'true');
          description.style.setProperty('display', 'none', 'important');
        }
      });

      formRoot.querySelectorAll('*').forEach((element) => {
        if (element.shadowRoot) simplifyFormRoot(element.shadowRoot);
        if (element.tagName === 'IFRAME') {
          try { simplifyFormRoot(element.contentDocument); } catch (_) { /* Cross-origin frames are left unchanged. */ }
        }
      });
    }

    function simplifyEmbeddedForm() {
      if (!appForm) return;
      simplifyFormRoot(appForm);
    }

    function openDialog() {
      if (!currentVariant || !isEligibleSize(currentVariant.size)) return;
      returnFocusTo = globalScope.document.activeElement;
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      simplifyEmbeddedForm();
      [250, 750, 1500, 3000].forEach((delay) => globalScope.setTimeout(simplifyEmbeddedForm, delay));
      globalScope.requestAnimationFrame(() => firstFormControl()?.focus());
    }

    function closeDialog() {
      if (typeof dialog.close === 'function' && dialog.open) dialog.close();
      else {
        dialog.removeAttribute('open');
        returnFocusTo?.focus?.();
      }
    }

    function focusableElements() {
      return [...dialog.querySelectorAll('button:not([disabled]),a[href],input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])')]
        .filter((element) => !element.hidden && element.getClientRects().length > 0);
    }

    openButton.addEventListener('click', openDialog);
    dialog.querySelector('[data-room-preview-close]').addEventListener('click', closeDialog);
    dialog.addEventListener('click', (event) => { if (event.target === dialog) closeDialog(); });
    dialog.addEventListener('close', () => returnFocusTo?.focus?.());
    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeDialog();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = focusableElements();
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && globalScope.document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && globalScope.document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    root.addEventListener('journey-home:variant-change', (event) => syncVariant(event.detail?.variant));
    syncVariant(byId(currentVariantId));
  }

  function scan(scope) {
    if (!scope || !globalScope.document) return;
    if (scope.matches?.('[data-aom-experience]')) initRoot(scope);
    scope.querySelectorAll?.('[data-aom-experience]').forEach(initRoot);
  }

  const api = { normalizeSize, isEligibleSize, initRoot, scan };

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  globalScope.JourneyHomeRoomPreview = api;

  if (globalScope.document) {
    scan(globalScope.document);
    if (!globalScope.__journeyHomeRoomPreviewSectionListener) {
      globalScope.__journeyHomeRoomPreviewSectionListener = true;
      globalScope.document.addEventListener('shopify:section:load', (event) => scan(event.target));
    }
  }
})(typeof window !== 'undefined' ? window : globalThis);
